import { useCallback, useEffect, useState } from "react"

import { api, type AlcanceDeConfiguracoes, type TipoDeSetorNaTela } from "../api"
import { Abas, type DescricaoDeAba } from "../componentes/Abas"
import { mensagemDeRecusa } from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { Card, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../componentes/AlertDialog"
import { ComDica } from "../componentes/ComDica"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import { Skeleton } from "../componentes/Skeleton"
import { toast } from "../componentes/Toast"
import * as Icones from "../componentes/Icones"
import { Input } from "../componentes/Input"
import { RotuloDoCampo, faltaNoFormulario } from "../componentes/RotuloDoCampo"
import { Selo } from "../componentes/Selo"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../componentes/Sheet"
import { Acao, Column, TablePrincipal2, TablePrincipalFiltroGlobal } from "../componentes/TablePrincipal2"
import { inteiro } from "../formato"
import { BOTAO_BRANCO, BOTAO_CANCELAR, CABECALHO_DO_SHEET, MAIUSCULA, RECUSA } from "../lib/classes"
import { cn } from "../lib/cn"

type AbaDosTipos = "todos" | "ativos" | "inativos"

const ABAS_DOS_TIPOS: DescricaoDeAba<AbaDosTipos>[] = [
  { chave: "todos", rotulo: "Todos", dica: "Mostra todos os tipos de setor" },
  { chave: "ativos", rotulo: "Ativos", dica: "Mostra só os tipos que aparecem nas listas de escolha" },
  { chave: "inativos", rotulo: "Inativos", dica: "Mostra só os tipos desativados" },
]

const POR_PAGINA = 10

type Gaveta = { modo: "novo" } | { modo: "editar"; tipo: TipoDeSetorNaTela } | null

const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase()

const setoresQueUsam = (quantos: number) =>
  quantos === 0
    ? "nenhum setor o usa"
    : quantos === 1
      ? "o setor que o usa continua com ele"
      : `os ${inteiro(quantos)} setores que o usam continuam com ele`

export function TiposDeSetor({
  alcance,
  aoVerOndeEUsado,
}: {
  alcance: AlcanceDeConfiguracoes
  aoVerOndeEUsado?: (nome: string) => void
}) {
  const [tipos, definirTipos] = useState<TipoDeSetorNaTela[] | null>(null)
  const [podeAlterar, definirPode] = useState(false)
  const [falha, definirFalha] = useState<string | null>(null)
  const [busca, definirBusca] = useState("")
  const [aba, definirAba] = useState<AbaDosTipos>("todos")
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(POR_PAGINA)
  const [gaveta, definirGaveta] = useState<Gaveta>(null)
  const [desativando, definirDesativando] = useState<TipoDeSetorNaTela | null>(null)

  const aplicar = useCallback((resposta: { dados: TipoDeSetorNaTela[]; pode_alterar: boolean }) => {
    definirTipos(resposta.dados)
    definirPode(resposta.pode_alterar)
    definirFalha(null)
  }, [])

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .tiposDeSetor()
      .then(aplicar)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [aplicar])

  useEffect(carregar, [carregar])

  if (tipos === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Tipos de setor</CardTitle>
        </CardHeader>
        {falha !== null ? (
          <CardContent className="flex flex-wrap items-center gap-3">
            <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
              {falha}
            </p>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={carregar}>
              <Icones.Recalcular />
              Tentar de novo
            </Button>
          </CardContent>
        ) : (
          <CardContent aria-busy="true" className="flex flex-col gap-3">
            <Skeleton className="h-10 w-1/2" />
            {[0, 1, 2, 3].map((linha) => (
              <Skeleton key={linha} className="h-9 w-full" />
            ))}
          </CardContent>
        )}
      </Card>
    )
  }

  const procurado = normalizar(busca)
  const filtrados = tipos.filter(
    (t) => (aba === "todos" || (aba === "ativos") === t.ativo) && (procurado === "" || normalizar(t.tipo).includes(procurado)),
  )
  const totalDePaginas = Math.max(1, Math.ceil(filtrados.length / porPagina))
  const paginaAtual = Math.min(pagina, totalDePaginas)
  const recorte = filtrados.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)

  const mudar = (tipo: TipoDeSetorNaTela, mudanca: { ativo: boolean }) => {
    api
      .alterarTipoDeSetor(tipo.id, mudanca)
      .then((resposta) => {
        aplicar(resposta)
        toast.success(`Tipo ${tipo.tipo} ${mudanca.ativo ? "reativado" : "desativado"}`)
      })
      .catch((erro: unknown) => toast.error(mensagemDeRecusa(erro)))
  }

  const vazio =
    tipos.length === 0 ? (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Etiqueta />
          </EmptyMedia>
          <EmptyTitle>Nenhum tipo de setor cadastrado</EmptyTitle>
          <EmptyDescription>{podeAlterar ? "Cadastre o primeiro tipo, usado na classificação dos setores." : "Só o Root cadastra."}</EmptyDescription>
        </EmptyHeader>
        {podeAlterar && (
          <EmptyContent>
            <Button type="button" className="h-10" onClick={() => definirGaveta({ modo: "novo" })}>
              <Icones.Mais />
              Adicionar tipo
            </Button>
          </EmptyContent>
        )}
      </Empty>
    ) : (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Buscar />
          </EmptyMedia>
          <EmptyTitle>Nada encontrado</EmptyTitle>
          <EmptyDescription>{busca !== "" ? `Nada casa com "${busca}".` : "Nada nesta situação."}</EmptyDescription>
        </EmptyHeader>
        {busca !== "" && (
          <EmptyContent>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => definirBusca("")}>
              Limpar a busca
            </Button>
          </EmptyContent>
        )}
      </Empty>
    )

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Tipos de setor{" "}
          <ComDica
            dica={
          podeAlterar
            ? "Os tipos valem para todas as entidades. Se desativado, sai das listas de escolha e continua nos setores que já o usam."
            : alcance.conta_de_plataforma
              ? "Os tipos valem para todas as entidades, inclusive as reais, e esta é a conta de demonstração: ela os consulta, e só o Root os altera."
              : "Os tipos valem para todas as entidades, e só o Root os altera."
          }
          />
        </CardTitle>
      </CardHeader>


      <CardContent>
        <TablePrincipal2
          data={recorte}
          pageCount={totalDePaginas}
          currentPage={paginaAtual}
          pageSize={porPagina}
          totalRegistros={filtrados.length}
          onPageChange={definirPagina}
          onPageSizeChange={(tamanho) => {
            definirPorPagina(tamanho)
            definirPagina(1)
          }}
          globalFilterValue={busca}
          onGlobalFilterChange={(valor) => {
            definirBusca(valor)
            definirPagina(1)
          }}
          mensagemVazia={vazio}
          listrada
          acaoNoTopo={
            podeAlterar && (
              <Button
                type="button"
                className="h-10"
                onClick={() => {
                  definirGaveta({ modo: "novo" })
                }}
              >
                <Icones.Mais />
                Adicionar tipo
              </Button>
            )
          }
          aoClicarNaLinha={podeAlterar ? (item) => definirGaveta({ modo: "editar", tipo: item }) : undefined}
          abasNaBarra={
            <Abas
              nome="tipos-de-setor"
              rotulo="Situação dos tipos de setor"
              classe="mb-0"
              abas={ABAS_DOS_TIPOS}
              atual={aba}
              aoTrocar={(chave) => {
                definirAba(chave)
                definirPagina(1)
              }}
            />
          }
        >
          <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Nome" />
          <Column<TipoDeSetorNaTela> name="tipo" label="Tipo" enableSorting={false} render={(valor) => <span className="font-medium">{String(valor)}</span>} />
          <Column<TipoDeSetorNaTela> name="setores" label="Setores" enableSorting={false} render={(valor) => inteiro(Number(valor))} />
          <Column<TipoDeSetorNaTela>
            name="ativo"
            label="Situação"
            enableSorting={false}
            render={(_valor, tipo) => <Selo situacao={tipo.ativo ? "ativa" : "inativa"}>{tipo.ativo ? "Ativo" : "Inativo"}</Selo>}
          />
          {podeAlterar && (
            <Acao<TipoDeSetorNaTela>
              label="Editar"
              icon={Icones.Anotacao}
             
              onClick={(tipo) => {
                definirGaveta({ modo: "editar", tipo })
              }}
            />
          )}
          <Acao<TipoDeSetorNaTela>
            label="Ver onde é usado"
            icon={Icones.Buscar}
            visivel={(item) => item.setores > 0}
            onClick={(item) => aoVerOndeEUsado?.(item.tipo)}
          />
          {podeAlterar && (
            <Acao<TipoDeSetorNaTela> label="Desativar" icon={Icones.Dispensar} visivel={(tipo) => tipo.ativo} onClick={definirDesativando} />
          )}
          {podeAlterar && (
            <Acao<TipoDeSetorNaTela> label="Reativar" icon={Icones.Reabrir} visivel={(tipo) => !tipo.ativo} onClick={(tipo) => mudar(tipo, { ativo: true })} />
          )}
        </TablePrincipal2>
      </CardContent>

      <AlertDialog open={desativando !== null} onOpenChange={(aberto) => !aberto && definirDesativando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader tom="perigo">
            <AlertDialogTitle>Desativar o tipo {desativando?.tipo}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Ele sai das listas de escolha; {setoresQueUsam(desativando?.setores ?? 0)}. É possível reativar depois.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                <Icones.Fechar />
                Cancelar
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (desativando !== null) mudar(desativando, { ativo: false })
                  definirDesativando(null)
                }}
              >
                <Icones.Dispensar />
                Desativar
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={gaveta !== null} onOpenChange={(aberta) => !aberta && definirGaveta(null)}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-lg [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">
              {gaveta?.modo === "editar" ? `Tipo de setor: ${gaveta.tipo.tipo}` : "Novo tipo de setor"}
            </SheetTitle>
          </SheetHeader>
          {gaveta !== null && (
            <FormularioDoTipo
              key={gaveta.modo === "editar" ? gaveta.tipo.id : "novo"}
              inicial={gaveta.modo === "editar" ? gaveta.tipo : null}
              tipos={tipos}
              aoCancelar={() => definirGaveta(null)}
              aoSalvar={(resposta, nome) => {
                aplicar(resposta)
                definirGaveta(null)
                toast.success(`Tipo ${normalizarParaMostrar(nome)} salvo`)
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </Card>
  )
}

const normalizarParaMostrar = (nome: string) => nome.trim().replace(/\s+/g, " ").toLocaleUpperCase("pt-BR")

function FormularioDoTipo({
  inicial,
  tipos,
  aoCancelar,
  aoSalvar,
}: {
  inicial: TipoDeSetorNaTela | null
  tipos: TipoDeSetorNaTela[]
  aoCancelar: () => void
  aoSalvar: (resposta: { dados: TipoDeSetorNaTela[]; pode_alterar: boolean }, nome: string) => void
}) {
  const [nome, definirNome] = useState(inicial?.tipo ?? "")
  const [enviando, definirEnviando] = useState(false)
  const [recusa, definirRecusa] = useState<string | null>(null)

  const repetido = tipos.find((t) => t.id !== inicial?.id && normalizar(t.tipo) === normalizar(nome) && normalizar(nome) !== "")
  const longo = nome.trim().length > 45
  const falta = faltaNoFormulario([["o tipo", nome]])
  const bloqueio = falta ?? (repetido ? `Já existe o tipo ${repetido.tipo}` : longo ? "O tipo passa de 45 caracteres" : null)

  const salvar = () => {
    definirEnviando(true)
    definirRecusa(null)
    const pedido = inicial === null ? api.criarTipoDeSetor(nome) : api.alterarTipoDeSetor(inicial.id, { tipo: nome })
    pedido
      .then((resposta) => aoSalvar(resposta, nome))
      .catch((erro: unknown) => definirRecusa(mensagemDeRecusa(erro)))
      .finally(() => definirEnviando(false))
  }

  return (
    <form
      className="flex flex-col gap-4 px-6 pt-3 pb-6"
      onSubmit={(evento) => {
        evento.preventDefault()
        if (bloqueio === null && !enviando) salvar()
      }}
    >
      <div>
        <RotuloDoCampo htmlFor="tipo-de-setor" obrigatorio>
          Tipo
        </RotuloDoCampo>
        <Input
          id="tipo-de-setor"
          required
          autoComplete="off"
          maxLength={60}
          value={nome}
          aria-invalid={repetido !== undefined || longo || undefined}
          aria-describedby={repetido || longo ? "tipo-de-setor-aviso" : undefined}
          onChange={(evento) => definirNome(evento.target.value)}
        />
        {(repetido || longo) && (
          <p id="tipo-de-setor-aviso" className={cn(RECUSA, "mt-1.5")}>
            {repetido ? `Já existe o tipo ${repetido.tipo}.` : "O tipo passa de 45 caracteres."}
          </p>
        )}
      </div>
      {recusa !== null && (
        <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
          {recusa}
        </p>
      )}
      <div className="mt-1 flex justify-end gap-2 border-t border-(--borda) pt-4">
        <Button type="button" variant="outline" className={BOTAO_CANCELAR} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button type="submit" disabled={bloqueio !== null || enviando} title={bloqueio ?? undefined}>
          <Icones.Salvar />
          {enviando ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </form>
  )
}
